import type { DayResult, DrillResult, MoveRecord, Progress, RunRecord } from '../game/types';
import { storage } from './storage';

const PROGRESS_KEY = 'wirespan:v1';
const DRILLS_KEY = 'wirespan:drills';
const SEEN_KEY = 'wirespan:seen';
const MUTED_KEY = 'wirespan:muted';
const HAPTICS_KEY = 'wirespan:haptics';
const MOTION_KEY = 'wirespan:motion';
const CONTRAST_KEY = 'wirespan:contrast';
const TEXT_KEY = 'wirespan:text';
const STUDY_WARNED_KEY = 'wirespan:studywarned';

export function emptyProgress(): Progress {
  return { results: {}, moves: {}, runs: {}, lastDay: 0, streak: 0 };
}

/** Accept only shapes we recognise — a corrupt blob shouldn't wipe out play. */
function parseProgress(raw: string | null): Progress {
  if (!raw) return emptyProgress();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return emptyProgress();
    const p = parsed as Partial<Progress>;
    return {
      results: p.results && typeof p.results === 'object' ? p.results : {},
      // Absent in saves from before move tracking, which is exactly how those
      // grids stay untracked.
      moves: p.moves && typeof p.moves === 'object' ? p.moves : {},
      // Absent in saves from before flawless and study marks existed. Missing
      // is not the same as false, but for these two it reads the same way:
      // nothing claimed, nothing marked.
      runs: p.runs && typeof p.runs === 'object' ? p.runs : {},
      lastDay: typeof p.lastDay === 'number' ? p.lastDay : 0,
      streak: typeof p.streak === 'number' ? p.streak : 0,
    };
  } catch {
    return emptyProgress();
  }
}

export async function loadProgress(): Promise<Progress> {
  return parseProgress(await storage.get(PROGRESS_KEY));
}

export async function saveProgress(progress: Progress): Promise<void> {
  await storage.set(PROGRESS_KEY, JSON.stringify(progress));
}

export async function hasSeenHelp(): Promise<boolean> {
  return (await storage.get(SEEN_KEY)) != null;
}

export async function markHelpSeen(): Promise<void> {
  await storage.set(SEEN_KEY, '1');
}

/** Sound is on unless the player has turned it off before. */
export async function loadMuted(): Promise<boolean> {
  return (await storage.get(MUTED_KEY)) === '1';
}

export async function saveMuted(muted: boolean): Promise<void> {
  await storage.set(MUTED_KEY, muted ? '1' : '0');
}

/** Haptics are on unless the player has turned them off before. */
export async function loadHaptics(): Promise<boolean> {
  return (await storage.get(HAPTICS_KEY)) !== '0';
}

export async function saveHaptics(on: boolean): Promise<void> {
  await storage.set(HAPTICS_KEY, on ? '1' : '0');
}

/** The player's explicit reduce-motion choice, or `null` to follow the OS. */
export async function loadMotionOverride(): Promise<boolean | null> {
  const raw = await storage.get(MOTION_KEY);
  return raw === null ? null : raw === '1';
}

export async function saveMotionOverride(reduced: boolean): Promise<void> {
  await storage.set(MOTION_KEY, reduced ? '1' : '0');
}

/** High contrast is off unless the player has turned it on before. */
export async function loadContrast(): Promise<boolean> {
  return (await storage.get(CONTRAST_KEY)) === '1';
}

export async function saveContrast(on: boolean): Promise<void> {
  await storage.set(CONTRAST_KEY, on ? '1' : '0');
}

/** The study warning has been shown and accepted once. */
export async function hasSeenStudyWarning(): Promise<boolean> {
  return (await storage.get(STUDY_WARNED_KEY)) != null;
}

export async function markStudyWarningSeen(): Promise<void> {
  await storage.set(STUDY_WARNED_KEY, '1');
}

/** Larger text is off unless the player has turned it on before. */
export async function loadLargeText(): Promise<boolean> {
  return (await storage.get(TEXT_KEY)) === '1';
}

export async function saveLargeText(on: boolean): Promise<void> {
  await storage.set(TEXT_KEY, on ? '1' : '0');
}

/**
 * Extend the streak when today's grid is first completed. Consecutive days
 * add up; a gap starts over. Replaying an already-recorded day is a no-op.
 */
export function bumpStreak(progress: Progress, today: number): Progress {
  if (progress.results[today]) return progress;
  return {
    ...progress,
    streak: progress.lastDay === today - 1 ? progress.streak + 1 : 1,
    lastDay: today,
  };
}

/** Is the streak still alive as of `today`? */
export function streakIsLive(progress: Progress, today: number): boolean {
  return progress.streak > 0 && progress.lastDay >= today - 1;
}

export function recordResult(progress: Progress, day: number, result: DayResult): Progress {
  return { ...progress, results: { ...progress.results, [day]: result } };
}

/**
 * The retired move count for a grid, or `null` if the game never took one.
 *
 * There is no writer any more (GDD §9). This reads what older versions
 * recorded so those days keep their reading; `null` stays `null` rather than
 * becoming a flattering zero.
 */
export function movesFor(progress: Progress, day: number): MoveRecord | null {
  return progress.moves[day] ?? null;
}

/**
 * Record how a grid is being played — the ever-removed flag, flawless, and
 * the study mark. Written as they change, never on completion (GDD §3.6).
 */
export function recordRun(progress: Progress, day: number, record: RunRecord): Progress {
  return { ...progress, runs: { ...progress.runs, [day]: record } };
}

/** How a grid was played, or `null` if nothing was ever recorded for it. */
export function runFor(progress: Progress, day: number): RunRecord | null {
  return progress.runs[day] ?? null;
}

/** Completed drills, keyed by the drill's permanent id. */
export async function loadDrills(): Promise<Record<string, DrillResult>> {
  const raw = await storage.get(DRILLS_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, DrillResult>) : {};
  } catch {
    return {};
  }
}

export async function saveDrills(drills: Record<string, DrillResult>): Promise<void> {
  await storage.set(DRILLS_KEY, JSON.stringify(drills));
}
